import React, { useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, PieChart, Pie } from "recharts";
import { TrendingUp, TrendingDown, ChefHat, Sparkles, ArrowUpDown, ChevronDown, ChevronUp } from "lucide-react";
import RecipeAIAdvisor from "./RecipeAIAdvisor";

const CAT_LABELS = {
  starter: "Entrant", main: "Principal", dessert: "Postres",
  drink: "Beguda", side: "Guarnició", sauce: "Salsa", bread: "Pa", other: "Altres",
};

function getMarginColor(margin) {
  if (margin >= 65) return { bg: "bg-emerald-100", text: "text-emerald-700", bar: "#10b981", label: "Alt", dot: "bg-emerald-500", border: "border-emerald-200" };
  if (margin >= 45) return { bg: "bg-amber-100", text: "text-amber-700", bar: "#f59e0b", label: "Acceptable", dot: "bg-amber-500", border: "border-amber-200" };
  return { bg: "bg-red-100", text: "text-red-700", bar: "#ef4444", label: "Baix", dot: "bg-red-500", border: "border-red-200" };
}

function SortHeader({ label, field, sortField, sortDir, onSort }) {
  const active = sortField === field;
  return (
    <th
      className="px-4 py-3 text-left text-xs font-semibold text-gray-500 cursor-pointer hover:text-gray-800 select-none whitespace-nowrap"
      onClick={() => onSort(field)}
    >
      <span className="flex items-center gap-1">
        {label}
        {active ? (
          sortDir === "desc" ? <ChevronDown className="w-3 h-3 text-[#7B2D3F]" /> : <ChevronUp className="w-3 h-3 text-[#7B2D3F]" />
        ) : (
          <ArrowUpDown className="w-3 h-3 text-gray-300" />
        )}
      </span>
    </th>
  );
}

function MarginBar({ value }) {
  const color = value >= 65 ? "bg-emerald-500" : value >= 45 ? "bg-amber-500" : "bg-red-500";
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${Math.min(Math.max(value, 0), 100)}%` }} />
      </div>
      <span className={`text-xs font-bold w-10 text-right ${value >= 65 ? "text-emerald-600" : value >= 45 ? "text-amber-600" : "text-red-600"}`}>
        {value.toFixed(1)}%
      </span>
    </div>
  );
}

export default function RecipeProfitability({ recipes }) {
  const [advisorRecipe, setAdvisorRecipe] = useState(null);
  const [sortField, setSortField] = useState("margin");
  const [sortDir, setSortDir] = useState("desc");
  const [selectedRecipe, setSelectedRecipe] = useState(null);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === "desc" ? "asc" : "desc");
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  const priced = recipes
    .filter(r => r.selling_price > 0 && r.estimated_cost > 0)
    .map(r => {
      const margin = ((r.selling_price - r.estimated_cost) / r.selling_price) * 100;
      const grossMargin = r.selling_price - r.estimated_cost;
      // Find most expensive ingredient
      const sortedIngs = (r.ingredients || [])
        .filter(i => parseFloat(i.unit_cost) > 0 && parseFloat(i.quantity) > 0)
        .map(i => ({ ...i, total: (parseFloat(i.unit_cost) || 0) * (parseFloat(i.quantity) || 0) }))
        .sort((a, b) => b.total - a.total);
      return { ...r, margin, grossMargin, topIngredients: sortedIngs };
    });

  const sorted = [...priced].sort((a, b) => {
    let diff = 0;
    if (sortField === "margin") diff = a.margin - b.margin;
    else if (sortField === "cost") diff = a.estimated_cost - b.estimated_cost;
    else if (sortField === "price") diff = a.selling_price - b.selling_price;
    else if (sortField === "gross") diff = a.grossMargin - b.grossMargin;
    return sortDir === "desc" ? -diff : diff;
  });

  if (priced.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center">
        <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center mx-auto mb-3">
          <ChefHat className="w-6 h-6 text-gray-400" />
        </div>
        <p className="text-sm font-medium text-gray-600 mb-1">Sense dades de rendibilitat</p>
        <p className="text-xs text-gray-400">Afegiu cost i preu de venda a les fitxes tècniques per veure l'anàlisi.</p>
      </div>
    );
  }

  const avgMargin = priced.reduce((s, r) => s + r.margin, 0) / priced.length;
  const avgCost = priced.reduce((s, r) => s + r.estimated_cost, 0) / priced.length;
  const totalRevenuePotential = priced.reduce((s, r) => s + r.selling_price, 0);
  const topDish = priced.reduce((a, b) => a.margin > b.margin ? a : b);
  const worstDish = priced.reduce((a, b) => a.margin < b.margin ? a : b);
  const lowMarginCount = priced.filter(r => r.margin < 45).length;

  const chartData = sorted.slice(0, 10).map(r => ({
    name: r.name.length > 14 ? r.name.slice(0, 12) + "…" : r.name,
    margin: parseFloat(r.margin.toFixed(1)),
    color: getMarginColor(r.margin).bar,
  }));

  // Distribution pie data
  const pieData = [
    { name: "Marge alt (≥65%)", value: priced.filter(r => r.margin >= 65).length, fill: "#10b981" },
    { name: "Acceptable (45-64%)", value: priced.filter(r => r.margin >= 45 && r.margin < 65).length, fill: "#f59e0b" },
    { name: "Baix (<45%)", value: priced.filter(r => r.margin < 45).length, fill: "#ef4444" },
  ].filter(d => d.value > 0);

  return (
    <div className="space-y-5">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Marge mitjà carta</p>
          <p className={`text-2xl font-bold ${getMarginColor(avgMargin).text}`}>{avgMargin.toFixed(1)}%</p>
          <div className={`inline-flex items-center gap-1 mt-1 text-[11px] font-semibold px-1.5 py-0.5 rounded-full ${getMarginColor(avgMargin).bg} ${getMarginColor(avgMargin).text}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${getMarginColor(avgMargin).dot}`} />
            {getMarginColor(avgMargin).label}
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Cost mitjà plat</p>
          <p className="text-2xl font-bold text-gray-800">{avgCost.toFixed(2)}€</p>
          <p className="text-[11px] text-gray-400 mt-1">{priced.length} plats analitzats</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Millor rendibilitat</p>
          <p className="text-sm font-bold text-emerald-700 truncate">{topDish?.name}</p>
          <p className="text-[11px] text-emerald-600 mt-1 flex items-center gap-1">
            <TrendingUp className="w-3 h-3" /> {topDish?.margin.toFixed(1)}% marge
          </p>
        </div>
        <div className={`rounded-2xl border p-4 ${lowMarginCount > 0 ? "bg-red-50 border-red-100" : "bg-white border-gray-100"}`}>
          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Marge baix</p>
          <p className={`text-2xl font-bold ${lowMarginCount > 0 ? "text-red-600" : "text-gray-800"}`}>{lowMarginCount}</p>
          <p className="text-[11px] text-gray-400 mt-1">plat{lowMarginCount !== 1 ? "s" : ""} per revisar</p>
        </div>
      </div>

      {/* Charts row */}
      <div className="grid sm:grid-cols-3 gap-4">
        {/* Bar chart */}
        <div className="sm:col-span-2 bg-white rounded-2xl border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold text-gray-800">Marge per plat (%)</h3>
            <div className="flex gap-1">
              {["margin", "cost", "price"].map(f => (
                <button
                  key={f}
                  onClick={() => setSortField(f)}
                  className={`text-[10px] font-semibold px-2 py-1 rounded-lg transition-all ${sortField === f ? "bg-[#7B2D3F] text-white" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}
                >
                  {f === "margin" ? "Marge" : f === "cost" ? "Cost" : "Preu"}
                </button>
              ))}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={chartData} margin={{ top: 0, right: 0, bottom: 0, left: -20 }}>
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#94a3b8" }} />
              <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} domain={[0, 100]} />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }}
                formatter={(v) => [`${v}%`, "Marge"]}
              />
              <Bar dataKey="margin" radius={[4, 4, 0, 0]}>
                {chartData.map((entry, i) => (
                  <Cell key={`entry-${i}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Pie distribution */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <h3 className="text-sm font-bold text-gray-800 mb-4">Distribució carta</h3>
          <ResponsiveContainer width="100%" height={160}>
            <PieChart>
              <Pie data={pieData} cx="50%" cy="50%" innerRadius={40} outerRadius={65} dataKey="value">
                {pieData.map((entry, i) => (
                  <Cell key={`entry-${i}`} fill={entry.fill} />
                ))}
              </Pie>
              <Tooltip formatter={(v) => [v, "plats"]} />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-1 mt-1">
            {pieData.map((d, i) => (
              <div key={`day-${i}`} className="flex items-center gap-2 text-[11px]">
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: d.fill }} />
                <span className="text-gray-600 flex-1 truncate">{d.name}</span>
                <span className="font-bold text-gray-800">{d.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Analytical table */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-sm font-bold text-gray-800">Anàlisi detallada per plat</h3>
          <p className="text-xs text-gray-400">{priced.length} plats amb dades de cost i preu</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 w-8">#</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Plat</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Categoria</th>
                <SortHeader label="Cost/ració" field="cost" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortHeader label="PVP" field="price" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortHeader label="Marge brut" field="gross" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortHeader label="% Marge" field="margin" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 w-36">Indicador</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500">IA</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {sorted.map((recipe, idx) => {
                const mc = getMarginColor(recipe.margin);
                const isExpanded = selectedRecipe === recipe.id;
                const isHighCost = recipe.estimated_cost > avgCost * 1.5;
                return (
                  <React.Fragment key={recipe.id}>
                    <tr
                      className={`hover:bg-gray-50/70 transition-colors cursor-pointer ${isExpanded ? "bg-gray-50/50" : ""}`}
                      onClick={() => setSelectedRecipe(isExpanded ? null : recipe.id)}
                    >
                      <td className="px-4 py-3 text-xs text-gray-300 font-mono">{idx + 1}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${mc.dot}`} />
                          <span className="font-semibold text-gray-800 text-sm">{recipe.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-gray-400">{CAT_LABELS[recipe.category] || "—"}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`font-semibold text-sm ${isHighCost ? "text-red-600" : "text-gray-700"}`}>
                          {recipe.estimated_cost.toFixed(2)}€
                        </span>
                        {isHighCost && <span className="ml-1 text-[10px] text-red-500 font-bold">↑ Alt</span>}
                      </td>
                      <td className="px-4 py-3 font-semibold text-gray-800 text-sm">{recipe.selling_price.toFixed(2)}€</td>
                      <td className="px-4 py-3">
                        <span className={`font-bold text-sm ${recipe.grossMargin > 0 ? "text-emerald-600" : "text-red-600"}`}>
                          {recipe.grossMargin.toFixed(2)}€
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 text-sm font-bold ${mc.text}`}>
                          {recipe.margin >= 65 ? <TrendingUp className="w-3.5 h-3.5" /> : recipe.margin < 45 ? <TrendingDown className="w-3.5 h-3.5" /> : null}
                          {recipe.margin.toFixed(1)}%
                        </span>
                      </td>
                      <td className="px-4 py-3 w-36">
                        <MarginBar value={recipe.margin} />
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={(e) => { e.stopPropagation(); setAdvisorRecipe(recipe); }}
                          className="p-1.5 rounded-lg hover:bg-[#f9f0f2] text-[#7B2D3F] transition-colors"
                          title="Anàlisi IA"
                        >
                          <Sparkles className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>

                    {/* Expanded ingredient breakdown */}
                    {isExpanded && recipe.topIngredients.length > 0 && (
                      <tr>
                        <td colSpan={9} className="px-6 py-4 bg-gray-50/70 border-b border-gray-100">
                          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Desglossat d'ingredients per cost</p>
                          <div className="grid sm:grid-cols-2 gap-2 max-w-xl">
                            {recipe.topIngredients.map((ing, i) => {
                              const pct = recipe.estimated_cost > 0 ? (ing.total / recipe.estimated_cost * 100) : 0;
                              return (
                                <div key={`ing-${i}`} className="flex items-center gap-2">
                                  <span className="text-xs text-gray-600 w-24 truncate">{ing.name}</span>
                                  <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                    <div
                                      className={`h-full rounded-full ${pct > 40 ? "bg-red-400" : pct > 25 ? "bg-amber-400" : "bg-blue-400"}`}
                                      style={{ width: `${Math.min(pct, 100)}%` }}
                                    />
                                  </div>
                                  <span className="text-[10px] text-gray-500 w-10 text-right">{ing.total.toFixed(2)}€</span>
                                  <span className={`text-[10px] font-bold w-9 text-right ${pct > 40 ? "text-red-600" : pct > 25 ? "text-amber-600" : "text-gray-400"}`}>
                                    {pct.toFixed(0)}%
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                          {recipe.margin < 45 && (
                            <p className="mt-2 text-[11px] text-red-600 font-medium">
                              ⚠ Per assolir un 65% de marge, el PVP hauria de ser <strong>{(recipe.estimated_cost / 0.35).toFixed(2)}€</strong> (actual: {recipe.selling_price.toFixed(2)}€)
                            </p>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="px-5 py-2.5 border-t border-gray-50 bg-gray-50/50 flex items-center gap-4 text-[11px] text-gray-400">
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Marge alt ≥65%</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-amber-500" /> Acceptable 45–64%</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-red-500" /> Baix &lt;45%</span>
          <span className="ml-auto">Feu clic a una fila per veure el detall · IA per suggeriments</span>
        </div>
      </div>

      {advisorRecipe && (
        <RecipeAIAdvisor
          recipe={advisorRecipe}
          onClose={() => setAdvisorRecipe(null)}
        />
      )}
    </div>
  );
}